<script setup lang="ts">
import type { TeacherArticle } from '~/models/TeacherArticle'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ContentArticle } from '~/models/Content/ContentArticle'
import type { ArticleContent } from '~/models/Content/ArticleContent'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { KsDialog } from '@aschehoug/kloss'
import arrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { TeleportationTarget } from '~/models/TeleportationTarget'
import { ContentType } from '~/models/Content/ContentType'
import useSlugify from '~/composables/useSlugify'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import TeacherSubitems from '~/components/subject/TeacherSubitems.vue'

const props = defineProps<{
  isOpen: boolean
  contentId: number | string
}>()

const emit = defineEmits(['closeGuide'])
const { t } = useI18n()

const { findContents } = useContentApi()
const { isTeacher } = storeToRefs(useAuthStore())
const { isTeacherArticle, isArticle, getTitle, isArticleContent, isPackage, packageTypes } = useContentHelper()
const { truthy } = arrayUtils()
const { slugify } = useSlugify()

const { data: article } = useQuery({
  enabled: computed(() => isTeacher.value && !props.isOpen && !!props.contentId),
  queryKey: computed(() => ['presentation-teacher', props.contentId]),
  queryFn: () => findContents<ContentArticle | TeacherArticle>({
    contentIdCriterion: [Number(props.contentId)],
    contentTypeCriterion: [ContentType.Article, ContentType.TeacherArticle],
    mainLocationCriterion: true,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
})

const articleRelationIds = computed(() => article.value
  && isTeacherArticle(article.value)
  && article.value.contents?.destinationContentIds?.length > 0
  ? article.value.contents.destinationContentIds
  : []
)

const { data: articleContents } = useQuery({
  enabled: computed(() => articleRelationIds.value.length > 0),
  staleTime: Infinity,
  queryKey: ['teacher-contents', props.contentId],
  queryFn: () => findContents<ArticleContent | ContentPackage>({
    contentIdCriterion: articleRelationIds.value,
    contentTypeCriterion: [...[ContentType.ArticleContent], ...packageTypes],
    mainLocationCriterion: true,
  }),
  select: (contents: Array<ArticleContent | ContentPackage>) => articleRelationIds.value
    .map(relationId => contents.find(({ contentId }) => contentId === Number(relationId)))
    .filter(truthy<ContentPackage | ArticleContent>)
})

const intro = computed(() => {
  if (article.value && isArticle(article.value))
    return article.value.body ?? ''
  if (article.value && isTeacherArticle(article.value))
    return article.value.intro ?? ''
  return ''
})
</script>

<template>
  <Teleport :to="TeleportationTarget.AppTop">
    <KsDialog
      :title="t('presentation.teacherGuide')"
      :open="isOpen"
      size="medium"
      @close="emit('closeGuide')"
    >
      <template #body>
        <article v-if="article" class="prose prose-au mx-auto prose-img:rounded-xl">
          <RichTextRenderer :text="intro" />
          <div v-if="Number(articleContents?.length) > 0">
            <div
              v-for="content in articleContents"
              :id="slugify(getTitle(content))"
              :key="content.contentId"
              class="last:mb-16"
            >
              <h2 v-text="getTitle(content)" />
              <TeacherSubitems
                v-if="isPackage(content)"
                :header="content"
              />
              <RichTextRenderer
                v-if="isArticleContent(content)"
                :text="content.body ?? ''"
              />
            </div>
          </div>
        </article>
      </template>
    </KsDialog>
  </Teleport>
</template>
